import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import SrgLinesItem from "./SrgLinesItem";
// import AssetsManagerBatchDelete from "./AssetsManagerBatchDelete";
import { TypographyNormal } from "components/customComponent";
import { colorStyling } from "helpers/color";
import {
  getSrgLines,
  // removeAssetsInBatch,
} from "../../../store/actions/smrt/srgLinesAction";
import {
  SET_SRGLINES_ROWS_PER_PAGE,
  SET_SRGLINES_FIRST_LOAD,
  SET_ERROR_PROMPT_SRGLINES_PAGE,
} from "../../../store/actionTypes/smrt/srgLinesActionType";
import { useDebounce } from "components/hooks";
import { logger } from "helpers/logger";
import { parseVariables } from "helpers/localize";
import { disabledLine, lineConfig } from "../helper/lineConfig";

const DATA_FETCH_LIMIT = 100;

const headerFields = [
  {
    id: "code",
    align: "center",
    labelID: "Code",
    width: "110px",
  },
  {
    id: "name",
    align: "left",
    labelID: "Name",
    width: "250px",
  },
  {
    id: "status",
    align: "left",
    labelID: "Status",
    width: "200px",
  },
  {
    id: "info",
    align: "left",
    labelID: "Info",
  },
  {
    id: "action",
    align: "center",
    labelID: "Action",
    width: "100px",
  },
];

const SrgLinesList = ({ permission }) => {
  const dispatch = useDispatch();

  const { t } = useTranslation("srgLines");
  const { t: tCommon } = useTranslation();

  const {
    firstLoad,
    linesData,
    linesFilter,
    rowsPerPage,
    loadingLinesData: loading,
  } = useSelector((state) => state.srgLines);

  const [currentPage, setCurrentPage] = useState(0);
  const [dataFrom, setDataFrom] = useState(0);
  const [dataOffset, setDataOffset] = useState(0);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [appendLoading, setAppendLoading] = useState(false);

  const tableDiv = useRef(null);

  const fetchContents = (payload) => {
    const { offset, append, filterUsed } = payload;

    const getPayload = {
      payload: {
        ...linesFilter,
        limit: DATA_FETCH_LIMIT,
        offset: offset ?? dataOffset,
      },
      cache: linesData,
      append: append,
      filter: filterUsed,
    };

    setDataOffset(offset);
    dispatch(
      getSrgLines(getPayload, () => {
        setAppendLoading(false);
      })
    );
    dispatch({ type: SET_ERROR_PROMPT_SRGLINES_PAGE, payload: false });
  };

  const updateDataList = (page, filterUsed) => {
    const nextSlice = page * rowsPerPage;
    setDataFrom(nextSlice);

    let appendData = false;
    if (linesData.items.length > 0 && nextSlice >= linesData.items.length) {
      appendData = true;
    }

    const nextOffset =
      Math.floor(nextSlice / DATA_FETCH_LIMIT) * DATA_FETCH_LIMIT;
    const payload = { offset: nextOffset, append: appendData, filterUsed };
    fetchContents(payload);
  };

  const debouncedUpdate = useDebounce(currentPage, 1111);
  useEffect(() => {
    if (updateFlag === true) {
      updateDataList(currentPage);
      setUpdateFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUpdate]);

  const updateDataSlice = ({ page, rows }) => {
    const nextPage = page >= 0 ? page : currentPage;
    const nextSlice = nextPage * (rows ?? rowsPerPage);
    if (nextSlice >= linesData.items.length) {
      setAppendLoading(true);
    }

    setDataFrom(nextSlice);
    setCurrentPage(nextPage);
    setUpdateFlag(true);

    if (tableDiv.current) {
      tableDiv.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleChangePagination = (e, page) => {
    updateDataSlice({ page });
    // setCheckboxAll(false);
  };

  const handleChangeRowsPerPage = (event) => {
    const updatedRows = parseInt(event.target.value, 10);
    dispatch({
      type: SET_SRGLINES_ROWS_PER_PAGE,
      payload: updatedRows,
    });
    updateDataSlice({ page: 0, rows: updatedRows });
    // setCheckboxAll(false);
  };

  const displayedRowsLabel = ({ from, to, count }) => {
    return parseVariables(tCommon("table.displayedRowsLabel"), {
      prefix: `${from}–${to}`,
      suffix:
        count !== -1
          ? count
          : `${tCommon("table.displayedRowsMoreThan")} ${to}`,
    });
  };

  useEffect(() => {
    updateDataList(0);
    // setSelectedItems({});
    return () => {
      dispatch({ type: SET_SRGLINES_FIRST_LOAD, payload: false });
      dispatch({ type: SET_ERROR_PROMPT_SRGLINES_PAGE, payload: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstLoad) {
      setCurrentPage(0);
      updateDataList(0, true);
    } else {
      dispatch({ type: SET_SRGLINES_FIRST_LOAD, payload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesFilter]);

  const dataSliced = useMemo(() => {
    const lastIndex = dataFrom + rowsPerPage;
    logger.log(`[${dataFrom}, ${lastIndex}]`);
    return linesData.items.slice(dataFrom, lastIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesData, dataFrom, rowsPerPage]);

  return (
    <>
      <Box sx={{ maxHeight: 567 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: "20vh" }}
          >
            <CircularProgress
              size={20}
              thickness={3}
              sx={{ color: colorStyling.primary }}
            />
            <TypographyNormal
              sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
            >
              {tCommon("loadingDataList")}
            </TypographyNormal>
          </Box>
        ) : (
          <>
            {linesData.items.length === 0 ? (
              <Paper elevation={2} sx={{ borderRadius: "6px", p: 2 }}>
                <Alert severity="info" variant="outlined">
                  <strong>{tCommon("noData")}</strong>
                </Alert>
              </Paper>
            ) : (
              <>
                <TableContainer
                  ref={tableDiv}
                  component={Paper}
                  sx={{ overflowY: "auto", maxHeight: 525, boxShadow: 3 }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {headerFields.map((header) => (
                          <TableCell
                            align={header.align}
                            key={header.id}
                            width={header.width}
                          >
                            {t(`table.header${header.labelID}`)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {dataSliced.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Box
                              sx={{ height: "20vh" }}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              flexDirection="column"
                            >
                              <CircularProgress
                                size={20}
                                thickness={3}
                                sx={{ color: colorStyling.primary }}
                              />
                              <TypographyNormal
                                sx={{
                                  color: colorStyling.primary,
                                  mt: 2,
                                  fontWeight: 400,
                                }}
                              >
                                {tCommon("loadingDataList")}
                              </TypographyNormal>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        dataSliced
                          .filter((line) => !disabledLine.includes(line.code))
                          .sort(
                            (a, b) =>
                              (lineConfig[a.code]?.order ?? 999) -
                              (lineConfig[b.code]?.order ?? 999)
                          )
                          .map((data, index) => (
                            <SrgLinesItem
                              pageData={{ page: currentPage, rowsPerPage }}
                              key={data.id + "-" + data.campaignName}
                              data={data}
                              index={index}
                              permission={permission}
                            />
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Box>

      {!loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 4,
            pb: 2,
            pr: 2,
          }}
        >
          <Paper component="div" elevation={1} sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={linesData.totalItem}
              page={currentPage}
              onPageChange={handleChangePagination}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={displayedRowsLabel}
              labelRowsPerPage={tCommon("table.rowsPerPageLabel")}
              disabled={appendLoading}
            />
          </Paper>
        </Box>
      )}
    </>
  );
};

export default SrgLinesList;
